import React, { useState, useEffect } from "react";
import {
  Package,
  Plus,
  Search,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Archive,
  Edit,
  Trash2,
  Filter,
  X,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../ui/alert-dialog";
import { Dialog, DialogContent } from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import StockFormModal from "./StockFormModal";
import stockService from "../../services/stockService";
import { showError, showInfo } from "../../store/errorStore";

/**
 * StocksManagement Component
 * Gerenciamento completo de estoque
 */
const StocksManagement = () => {
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingStock, setEditingStock] = useState(null);
  const [savingStock, setSavingStock] = useState(false);
  const [loadingModal, setLoadingModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingStockId, setDeletingStockId] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [filterExpiration, setFilterExpiration] = useState("all");

  useEffect(() => {
    loadData();
  }, []);

  /**
   * Carregar todos os dados
   */
  const loadData = async () => {
    setLoading(true);
    try {
      await fetchStocks();
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar dados",
        "Erro de Carregamento"
      );
      setStocks([]);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Buscar todos os estoques
   */
  const fetchStocks = async () => {
    try {
      const data = await stockService.getAllStocks();
      setStocks(Array.isArray(data) ? data : []);
    } catch (error) {
      showError(
        error.response?.data?.message || "Erro ao carregar estoque",
        "Erro de Carregamento"
      );
      setStocks([]);
    }
  };

  /**
   * Abrir modal para criar novo estoque
   */
  const handleCreateStock = () => {
    setEditingStock(null);
    setShowModal(true);
  };

  /**
   * Abrir modal para editar estoque
   */
  const handleEditStock = async (stock) => {
    setLoadingModal(true);
    setEditingStock(stock);

    // Aguarda um momento para garantir que os dados estão carregados
    setTimeout(() => {
      setShowModal(true);
      setLoadingModal(false);
    }, 500);
  };

  /**
   * Salvar estoque (criar ou atualizar)
   */
  const handleSaveStock = async (stockData) => {
    setSavingStock(true);
    try {
      if (editingStock) {
        // Atualizar estoque existente
        await stockService.updateStock(editingStock.id, stockData);
        showInfo("Estoque atualizado com sucesso", "Estoque Atualizado");
      } else {
        // Criar novo estoque
        await stockService.createStock(stockData);
        showInfo("Entrada de estoque criada com sucesso", "Estoque Criado");
      }

      setShowModal(false);
      setEditingStock(null);
      await fetchStocks();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Erro ao salvar estoque";
      showError(errorMessage, "Erro");
    } finally {
      setSavingStock(false);
    }
  };

  /**
   * Confirmar exclusão de estoque
   */
  const handleDeleteClick = (stock) => {
    setDeletingStockId(stock.id);
    setShowDeleteDialog(true);
  };

  /**
   * Executar exclusão de estoque
   */
  const handleConfirmDelete = async () => {
    if (!deletingStockId) return;

    try {
      await stockService.deleteStock(deletingStockId);
      showInfo("Entrada de estoque removida com sucesso", "Estoque Deletado");
      await fetchStocks();
    } catch (error) {
      const errorMessage =
        error.response?.data?.message ||
        error.response?.data?.error ||
        "Erro ao deletar estoque";
      showError(errorMessage, "Erro");
    } finally {
      setShowDeleteDialog(false);
      setDeletingStockId(null);
    }
  };

  /**
   * Filtrar estoques
   */
  const filteredStocks = stocks.filter((stock) => {
    // Filtro de busca
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const productName = (stock.product_name || "").toLowerCase();
      const lotCode = (stock.lot_code || "").toLowerCase();
      const locationName = (stock.storage_location_name || "").toLowerCase();

      if (
        !productName.includes(query) &&
        !lotCode.includes(query) &&
        !locationName.includes(query)
      ) {
        return false;
      }
    }

    // Filtro de status
    if (filterStatus !== "all") {
      const available = stockService.calculateAvailableQuantity(
        stock.current_quantity,
        stock.reserved_quantity
      );

      if (filterStatus === "available" && available <= 0) return false;
      if (filterStatus === "low" && (available > 10 || available <= 0))
        return false;
      if (filterStatus === "out" && available > 0) return false;
    }

    // Filtro de validade
    if (filterExpiration !== "all") {
      const days = stockService.calculateDaysUntilExpiration(
        stock.expiration_date
      );

      if (filterExpiration === "expired" && days >= 0) return false;
      if (filterExpiration === "expiring" && (days < 0 || days > 30))
        return false;
      if (filterExpiration === "valid" && days <= 30) return false;
    }

    return true;
  });

  /**
   * Calcular estatísticas
   */
  const calculateStats = () => {
    const totalItems = stocks.length;
    const totalValue = stocks.reduce(
      (sum, stock) =>
        sum +
        stockService.calculateTotalValue(
          stock.current_quantity,
          stock.unit_cost
        ),
      0
    );
    const totalQuantity = stocks.reduce(
      (sum, stock) => sum + (stock.current_quantity || 0),
      0
    );
    const totalReserved = stocks.reduce(
      (sum, stock) => sum + (stock.reserved_quantity || 0),
      0
    );

    const expiredItems = stocks.filter((stock) => {
      const days = stockService.calculateDaysUntilExpiration(
        stock.expiration_date
      );
      return days < 0;
    }).length;

    const expiringItems = stocks.filter((stock) => {
      const days = stockService.calculateDaysUntilExpiration(
        stock.expiration_date
      );
      return days >= 0 && days <= 30;
    }).length;

    const lowStockItems = stocks.filter((stock) => {
      const available = stockService.calculateAvailableQuantity(
        stock.current_quantity,
        stock.reserved_quantity
      );
      return available > 0 && available <= 10;
    }).length;

    const outOfStockItems = stocks.filter((stock) => {
      const available = stockService.calculateAvailableQuantity(
        stock.current_quantity,
        stock.reserved_quantity
      );
      return available <= 0;
    }).length;

    return {
      totalItems,
      totalValue,
      totalQuantity,
      totalReserved,
      expiredItems,
      expiringItems,
      lowStockItems,
      outOfStockItems,
    };
  };

  const stats = calculateStats();

  /**
   * Limpar filtros
   */
  const clearFilters = () => {
    setSearchQuery("");
    setFilterStatus("all");
    setFilterExpiration("all");
  };

  const hasActiveFilters =
    searchQuery || filterStatus !== "all" || filterExpiration !== "all";

  return (
    <div className="space-y-6">
      {/* Modal de Loading */}
      <Dialog open={loading} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando estoque...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-6 w-6 sm:h-8 sm:w-8" />
            Gestão de Estoque
          </h1>
          <p className="text-sm text-muted-foreground">
            Controle completo do estoque de produtos
          </p>
        </div>
        <Button
          onClick={handleCreateStock}
          size="lg"
          className="w-full sm:w-auto"
        >
          <Plus className="mr-2 h-5 w-5" />
          Nova Entrada
        </Button>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Valor Total em Estoque
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stockService.formatCurrency(stats.totalValue)}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.totalItems} itens diferentes
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">
              Quantidade Total
            </CardTitle>
            <Archive className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalQuantity}</div>
            <p className="text-xs text-muted-foreground">
              {stats.totalReserved} reservadas
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Alertas</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.lowStockItems + stats.outOfStockItems}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.lowStockItems} baixo · {stats.outOfStockItems} esgotado
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Validade</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {stats.expiredItems + stats.expiringItems}
            </div>
            <p className="text-xs text-muted-foreground">
              {stats.expiredItems} vencido · {stats.expiringItems} a vencer
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filtros e Busca */}
      <Card>
        <CardHeader>
          <div className="flex flex-col gap-4">
            {/* Campo de Busca - Ocupa linha inteira no mobile */}
            <div className="relative w-full">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Buscar por produto, lote ou local..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 w-full"
              />
            </div>

            {/* Filtros em linha separada */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os Status</SelectItem>
                  <SelectItem value="available">Disponível</SelectItem>
                  <SelectItem value="low">Estoque Baixo</SelectItem>
                  <SelectItem value="out">Esgotado</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterExpiration}
                onValueChange={setFilterExpiration}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="Validade" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as Validades</SelectItem>
                  <SelectItem value="expired">Vencidos</SelectItem>
                  <SelectItem value="expiring">A Vencer (30d)</SelectItem>
                  <SelectItem value="valid">Válidos</SelectItem>
                </SelectContent>
              </Select>
              {hasActiveFilters && (
                <Button
                  variant="outline"
                  size="icon"
                  onClick={clearFilters}
                  title="Limpar filtros"
                  className="w-full sm:w-auto"
                >
                  <X className="h-4 w-4 mr-2 sm:mr-0" />
                  <span className="sm:hidden">Limpar Filtros</span>
                </Button>
              )}
            </div>

            <div className="flex items-center justify-between text-sm text-muted-foreground">
              <span>
                {filteredStocks.length} de {stocks.length} registros
              </span>
              {hasActiveFilters && (
                <span className="text-blue-600">Filtros ativos</span>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Tabela de Estoque */}
      <Card>
        <CardContent className="p-0">
          {filteredStocks.length === 0 ? (
            <div className="text-center py-12">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500 mb-2">
                {stocks.length === 0
                  ? "Nenhuma entrada de estoque cadastrada"
                  : "Nenhum resultado encontrado"}
              </p>
              {stocks.length === 0 && (
                <Button onClick={handleCreateStock} variant="outline">
                  <Plus className="mr-2 h-4 w-4" />
                  Criar Primeira Entrada
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="hidden md:table-cell w-[80px]">
                      ID
                    </TableHead>
                    <TableHead>Produto</TableHead>
                    <TableHead className="hidden lg:table-cell">Lote</TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Local
                    </TableHead>
                    <TableHead className="hidden md:table-cell text-center">
                      Quantidade
                    </TableHead>
                    <TableHead className="text-center">Disponível</TableHead>
                    <TableHead className="hidden md:table-cell text-center">
                      Status
                    </TableHead>
                    <TableHead className="hidden lg:table-cell">
                      Validade
                    </TableHead>
                    <TableHead className="hidden lg:table-cell text-right">
                      Custo Unit.
                    </TableHead>
                    <TableHead className="hidden lg:table-cell text-right">
                      Valor Total
                    </TableHead>
                    <TableHead className="text-right w-[100px]">
                      Ações
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredStocks.map((stock) => {
                    const available = stockService.calculateAvailableQuantity(
                      stock.current_quantity,
                      stock.reserved_quantity
                    );
                    const stockStatus = stockService.getStockStatus(
                      stock.current_quantity,
                      stock.reserved_quantity
                    );
                    const expirationStatus = stockService.getExpirationStatus(
                      stock.expiration_date
                    );
                    const totalValue = stockService.calculateTotalValue(
                      stock.current_quantity,
                      stock.unit_cost
                    );

                    return (
                      <TableRow key={stock.id}>
                        <TableCell className="hidden md:table-cell font-mono text-sm text-gray-500">
                          #{stock.id}
                        </TableCell>
                        <TableCell>
                          <div>
                            <div className="font-medium">
                              {stock.product_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {stockService.formatCurrency(stock.product_price)}
                            </div>
                            {/* Info extra visível apenas no mobile */}
                            <div className="md:hidden text-xs text-gray-500 mt-1 space-y-1">
                              <div>Lote: {stock.lot_code}</div>
                              <div>{stock.storage_location_name}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <code className="text-sm bg-gray-100 px-2 py-1 rounded">
                            {stock.lot_code}
                          </code>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {stock.storage_location_name}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center font-semibold">
                          {stock.current_quantity}
                          {stock.reserved_quantity > 0 && (
                            <div className="text-xs text-gray-500">
                              -{stock.reserved_quantity} reserv.
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={`font-semibold text-base ${
                              available <= 0
                                ? "text-red-600"
                                : available <= 10
                                ? "text-yellow-600"
                                : "text-green-600"
                            }`}
                          >
                            {available}
                          </span>
                          {/* Badge de status visível no mobile */}
                          <div className="md:hidden mt-1">
                            <Badge
                              variant={stockStatus.variant}
                              className="text-xs"
                            >
                              {stockStatus.label}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-center">
                          <Badge variant={stockStatus.variant}>
                            {stockStatus.label}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell">
                          <div>
                            <Badge
                              variant={expirationStatus.variant}
                              className="mb-1"
                            >
                              {expirationStatus.label}
                            </Badge>
                            <div className="text-xs text-gray-500">
                              {stockService.formatDate(stock.expiration_date)}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-right">
                          {stockService.formatCurrency(stock.unit_cost)}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-right font-semibold">
                          {stockService.formatCurrency(totalValue)}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 sm:gap-2">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleEditStock(stock)}
                              className="h-8 w-8 sm:h-10 sm:w-10"
                            >
                              <Edit className="h-3 w-3 sm:h-4 sm:w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteClick(stock)}
                              className="h-8 w-8 sm:h-10 sm:w-10"
                            >
                              <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 text-red-500" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Modal de Formulário */}
      <StockFormModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingStock(null);
        }}
        onSave={handleSaveStock}
        stock={editingStock}
        isSaving={savingStock}
      />

      {/* Loading Overlay ao abrir modal de edição */}
      <Dialog open={loadingModal} onOpenChange={() => {}}>
        <DialogContent
          className="max-w-sm"
          onInteractOutside={(e) => e.preventDefault()}
        >
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative">
              <div className="w-16 h-16 border-4 border-gray-200 border-t-[#c9332C] rounded-full animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <Package className="h-6 w-6 text-[#c9332C]" />
              </div>
            </div>
            <p className="mt-6 text-base font-semibold text-gray-900 text-center">
              Carregando dados do estoque...
            </p>
            <p className="mt-2 text-sm text-gray-500 text-center">
              Por favor, aguarde
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar Exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover esta entrada de estoque? Esta ação
              não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Deletar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default StocksManagement;
